// $FlowFixMe
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedDate } from 'react-intl';
import { bindActionCreators } from 'redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { pathOr } from 'ramda';
import {
  Box,
  Stack,
  Button,
  Spinner,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import confirm from '../../../../../../components/ConfirmDialogModal';
import ElementWithPermissions from '../../../../../../components/ElementWithPermissions';
import ConditionalTooltip from '../../../../../../components/ConditionalTooltip';
import { formatProgramList } from '../../../../../../utils/affordable';

import { editLease, sendToPortal } from '../../../../actions';
import {
  PORTAL_ACTION_SENT,
  PORTAL_ACTION_EXECUTED,
  PORTAL_SIGNATURE_STATUS_SIGNED,
} from '../../../../../../utils/lease-helpers';

import ExecuteSignatureModal from '../ButtonActionModals/ExecuteSignatureModal';
import messages from './messages';

type ElectronicMethodButtonsProps = {
  allAffordableTabsApproved: boolean,
  applicationId: string,
  canComplete: boolean,
  difference: number,
  hasNonFR: boolean,
  isResident: boolean,
  leaseId: string,
  leaseSignatureStatuses: Object,
  onGenerate: Function,
  profileId: string,
  prospectId: string,
  transferCertBlocked?: boolean,
  transferCertTooltip?: string,
  unapprovedAffordablePrograms?: Array<string>,
  moveInCertBlocked?: boolean,
  moveInCertTooltip?: string,
};

type ElectronicMethodButtonsInjectedProps = {
  actions: Object,
  intl: Object,
};

export const ElectronicMethodButtons = ({
  actions,
  allAffordableTabsApproved,
  applicationId,
  canComplete,
  difference,
  hasNonFR,
  intl,
  isResident,
  leaseId,
  leaseSignatureStatuses,
  onGenerate,
  profileId,
  prospectId,
  transferCertBlocked = false,
  transferCertTooltip = '',
  unapprovedAffordablePrograms = [],
  moveInCertBlocked = false,
  moveInCertTooltip = '',
}: ElectronicMethodButtonsProps & ElectronicMethodButtonsInjectedProps) => {
  const [isEditLeaseMode, setIsEditLeaseMode] = useState(false);

  const [showExecuteModal, setShowExecuteModal] = useState(false);

  const handleShowExecuteClick = () => setShowExecuteModal(true);
  const handleHideExecuteClick = () => setShowExecuteModal(false);

  const { latestLog, signatures } = leaseSignatureStatuses;

  const confirmEditDialog = () => {
    confirm(intl.formatMessage(messages.confirmEditElectronicLease)).then(
      () => {
        actions.editLease(
          leaseId,
          profileId,
          isResident,
          prospectId,
          undefined,
          undefined,
          setIsEditLeaseMode,
        );
      },
    );
  };

  const handleSendToPortalClick = () =>
    actions.sendToPortal(leaseId, profileId, isResident, prospectId);

  const portalAction = pathOr(null, ['action'], latestLog);
  const portalActionDate = pathOr(null, ['createdAt'], latestLog);
  const notSignedLeases = signatures.filter(
    (sig) => sig.signatureStatus !== PORTAL_SIGNATURE_STATUS_SIGNED,
  );

  const leaseSent = portalAction === PORTAL_ACTION_SENT;
  const leaseExecuted = portalAction === PORTAL_ACTION_EXECUTED;

  const { automateMonthlyLeaseTransactionsMvp: automateMLTsFlag } = useFlags();
  const notReconciled = automateMLTsFlag ? false : difference !== 0;

  // Three gates, one tooltip, each naming its own programs upstream: `transferCertTooltip` from
  // useTransferLeaseCertGate (resident-side), `moveInCertTooltip` from LeaseDataTabForm (the
  // applicant/move-in gate), and the affordable compliance gate formatted here. The `||` chain is
  // the precedence when more than one can name a program: move-in supersedes the tab-surfaced
  // compliance status on the applicant flow (it catches the HUD case that status misses). The
  // compliance generic fallback keeps the copy off a bare "There is an open certification".
  // Branch on the formatted string, not the raw length: formatProgramList drops empty/whitespace
  // names, so a blank entry falls through to the generic copy rather than interpolating nothing.
  const complianceProgramList = formatProgramList(unapprovedAffordablePrograms);
  const complianceGateTooltip = complianceProgramList
    ? intl.formatMessage(messages.certificationsNotApproved, {
        programs: complianceProgramList,
      })
    : intl.formatMessage(messages.certificationsNotApprovedGeneric);
  const certGateTooltip =
    transferCertTooltip || moveInCertTooltip || complianceGateTooltip;
  const showCertGateTooltip =
    !allAffordableTabsApproved || moveInCertBlocked || transferCertBlocked;

  return (
    <>
      <ExecuteSignatureModal
        applicationId={applicationId}
        isResident={isResident}
        leaseId={leaseId}
        onHide={handleHideExecuteClick}
        profileId={profileId}
        prospectId={prospectId}
        show={showExecuteModal}
      />
      <ElementWithPermissions scope={['lease-create']}>
        <Stack spacing={1} direction={'row'} justifyContent={'center'}>
          <Button
            sx={{ flex: 1, maxWidth: '150px' }}
            data-testid={'editLeaseButton'}
            variant={'primarySubtle'}
            onClick={confirmEditDialog}
            disabled={isEditLeaseMode}
          >
            {isEditLeaseMode ? (
              <Spinner small />
            ) : (
              intl.formatMessage(messages.edit)
            )}
          </Button>
          <>
            {!isEditLeaseMode && (
              <>
                {!leaseSent && !leaseExecuted && (
                  <ConditionalTooltip
                    containerSX={{
                      display: 'flex',
                      flex: 1,
                      maxWidth: '150px',
                    }}
                    show={showCertGateTooltip}
                    title={certGateTooltip}
                    placement={'top'}
                    variant={'light'}
                  >
                    {/* span wrapper: a disabled MUI button swallows pointer events. Carries the
                        flex sizing itself since it — not the Button — is now the Stack's flex item.
                        ElementWithPermissions sits inside so the Button is its direct cloned child:
                        the outer lease-create wrapper's only direct child is the <Stack>, so the
                        Stack absorbs that clone and the buttons never see it. */}
                    <ElementWithPermissions scope={['lease-create']}>
                      <Button
                        sx={{ flex: 1, maxWidth: '150px' }}
                        data-testid={'sendToPortalButton'}
                        variant={'shout'}
                        onClick={handleSendToPortalClick}
                        disabled={
                          !canComplete ||
                          notReconciled ||
                          hasNonFR ||
                          !allAffordableTabsApproved ||
                          moveInCertBlocked ||
                          transferCertBlocked
                        }
                      >
                        {intl.formatMessage(messages.sendToPortal)}
                      </Button>
                    </ElementWithPermissions>
                  </ConditionalTooltip>
                )}
                {leaseSent && !leaseExecuted && (
                  <ConditionalTooltip
                    containerSX={{
                      display: 'flex',
                      flex: 1,
                      maxWidth: '150px',
                    }}
                    show={showCertGateTooltip}
                    title={certGateTooltip}
                    placement={'top'}
                    variant={'light'}
                  >
                    {/* span wrapper: a disabled MUI button swallows pointer events.
                        ElementWithPermissions stays inside the span so the Button remains its
                        direct cloned child and still receives the permission-driven `disabled`.
                        Carries the flex sizing itself since it — not the Button — is now the
                        Stack's flex item. */}
                    <ElementWithPermissions scope={['lease-execute']}>
                      <Button
                        sx={{ flex: 1, maxWidth: '150px' }}
                        variant={'shout'}
                        data-testid={'executeLeaseButton'}
                        onClick={handleShowExecuteClick}
                        disabled={
                          notSignedLeases.length > 0 ||
                          hasNonFR ||
                          !allAffordableTabsApproved ||
                          moveInCertBlocked ||
                          transferCertBlocked
                        }
                      >
                        {intl.formatMessage(messages.executeLease)}
                      </Button>
                    </ElementWithPermissions>
                  </ConditionalTooltip>
                )}
                <ConditionalTooltip
                  containerSX={{ display: 'flex', flex: 1, maxWidth: '150px' }}
                  show={showCertGateTooltip}
                  title={certGateTooltip}
                  placement={'top'}
                  variant={'light'}
                >
                  {/* span wrapper: a disabled MUI button swallows pointer events. Carries the
                      flex sizing itself since it — not the Button — is now the Stack's flex item.
                      ElementWithPermissions sits inside for the same reason as Send to Portal. */}
                  <ElementWithPermissions scope={['lease-create']}>
                    <Button
                      sx={{ flex: 1, maxWidth: '150px' }}
                      data-testid={'downloadLeaseButton'}
                      variant={'primary'}
                      disabled={
                        !canComplete ||
                        notReconciled ||
                        hasNonFR ||
                        !allAffordableTabsApproved ||
                        moveInCertBlocked ||
                        transferCertBlocked
                      }
                      onClick={onGenerate}
                    >
                      {intl.formatMessage(messages.downloadLease)}
                    </Button>
                  </ElementWithPermissions>
                </ConditionalTooltip>
              </>
            )}
          </>
        </Stack>
        <Box marginTop={1}>
          {leaseSent &&
            !leaseExecuted &&
            portalActionDate &&
            !isEditLeaseMode && (
              <div className="text-center" style={{ marginRight: '3%' }}>
                {`${intl.formatMessage(messages.sentToPortal)} - `}
                <FormattedDate value={portalActionDate} />
              </div>
            )}
          {notReconciled && (
            <div className="text-center" style={{ marginRight: '3%' }}>
              {intl.formatMessage(messages.notReconciled)}
            </div>
          )}
        </Box>
      </ElementWithPermissions>
    </>
  );
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators({ editLease, sendToPortal }, dispatch);
  return { actions };
}

const InjectedElectronicMethodButtons = injectIntl(ElectronicMethodButtons);

export default connect(
  null,
  mapDispatchToProps,
)(InjectedElectronicMethodButtons);
